const BASE_URL = import.meta.env.VITE_API_URL;
const TIMEOUT_DURATION = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// function to refresh user session 
export const refreshSession = async() => {
    // url for refresh endpoint
    const url = `${BASE_URL}/accounts/refreshSession`;
	try {
		// try to get new session
		const res = await fetch(url, {
            method: 'GET',
            credentials: "include"
        });
		return res.status !== 401;
	} catch(error) {
		return false;
	}
};

// function to validate initial session
export const validateSession = async() => {
    // url for session endpoint
    const url = `${BASE_URL}/accounts/validateSession`;
	try{
		const response = await fetch(url, {
            method: 'GET',
            credentials: "include"
        });

		if(response.status === 200) {
			return true;
		}
		
		// Only try refresh if validation fails
		return await refreshSession();
	} catch(error) {
        return false;
    }
};

const timeoutPromise = (ms) => {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject({
                status: 408,
                message: 'Request Timeout',
                error: 'The request took too long to complete'
            });
        }, ms);
    });
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const shouldRetry = (status) => {
    // Don't retry for these status codes
    const noRetryStatuses = [400, 401, 403, 404, 422];
    return !noRetryStatuses.includes(status);
};

// function to handle API requests 
const apiService = async(endpoint, method = 'GET', body = null, headers = {}, retryCount = 0) => {
	let url = `${BASE_URL}/${endpoint}`;

	const options = {
		method: method,
		body: body && JSON.stringify(body),
		credentials: "include",
		headers: {
			'Content-Type': 'application/json',
			...headers
		}
	};

	try {
		// fetch api resources
		const response = await Promise.race([
			fetch(url, options),
			timeoutPromise(TIMEOUT_DURATION)
		]);

		// Return a Promise that resolves with the payload
		if(response.ok) {
			const payload = await response.json();
			return Promise.resolve(payload);
		} 
		
		// Unauthorized - attempt to refresh session
		if(response.status === 401) {
			const sessionCreated = await refreshSession();
			if(sessionCreated) {
				return apiService(endpoint, method, body, headers);
			}
            return Promise.reject({
                status: 401,
                message: 'Session Expired',
                error: 'Authentication failed'
            });
		}

		// Forbidden - user logged in but unauthorized to access resource
		if(response.status === 403) {
			return Promise.reject({
				status: 403,
				message: 'Forbidden',
				error: 'User is not authorized to access this resource'
			});
		}

        // Not Modified - resource has not been modified since last request
		if(response.status === 304) {
			return Promise.reject({
				status: 304,
				message: 'Not Modified',
				error: 'Resource has not been modified since last request'
			});
		}

		// Handle retries for appropriate status codes
        if(retryCount < MAX_RETRIES && shouldRetry(response.status)) {
            await sleep(RETRY_DELAY * Math.pow(2, retryCount));
            return apiService(endpoint, method, body, headers, retryCount + 1);
        }

		// Handle errors
		const errorData = await response.json().catch(() => null);
		return Promise.reject({
			status: response.status,
			message: errorData?.message || 'Server Error',
			error: errorData
		});
	} catch(error) {
		if(error.status === 408) {
            return Promise.reject(error);
        }

        // Retry network errors
        if(retryCount < MAX_RETRIES) {
            await sleep(RETRY_DELAY * Math.pow(2, retryCount));
            return apiService(endpoint, method, body, headers, retryCount + 1);
        }

        // Network or parsing errors
        return Promise.reject({
            status: 0,
            message: 'Network Error',
            error: error.message
        });
    }
};

export default apiService;